// filepath: src/components/Auth.js
// PURPOSE: Stay on login for bad creds + add "Reset password" button that navigates to /reset-password

import React, { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { auth, db } from "../firebase";
import {
  fetchSignInMethodsForEmail,
  createUserWithEmailAndPassword,
} from "firebase/auth";
import { doc, getDoc, setDoc, serverTimestamp } from "firebase/firestore";
import { friendlyFromError, friendlyAuthMessage } from "../utils/friendlyError";
import { signInWithEmailAndPasswordDebug } from "../lib/authDebug";
import { isHostedCheckoutLink } from "../lib/planLimits";




export default function Auth({ onLogin }) {
  const navigate = useNavigate();
  const location = useLocation();

  // Show a tiny banner when returning from Stripe (?paid=1 or ?session_id=...) or after account creation (?created=1)
  useEffect(() => {
    try {
      const sp = new URLSearchParams(location.search || "");
      const created = sp.get("created") === "1";
      if (created) {
        setHint("Account created successfully. Please sign in to continue.");
        sp.delete("created");
        const next = sp.toString();
        navigate({ pathname: location.pathname, search: next ? `?${next}` : "" }, { replace: true });
        const t = setTimeout(() => setHint(""), 8000);
        return () => clearTimeout(t);
      }
    } catch {}
  }, [location.search, location.pathname, navigate]);

  useEffect(() => {
    try {
      const sp = new URLSearchParams(location.search || "");
      const paid = sp.get("paid") === "1";
      const sid  = sp.get("session_id");
      if (paid || sid) {
        if (sid) try { localStorage.setItem("stripe_last_session_id", sid); } catch {}
        setHint("Payment successful. Please sign in to continue.");
        try { if (typeof suppressStripeRedirectFor === "function") suppressStripeRedirectFor(10); } catch {}
        sp.delete("paid");
        if (sid) sp.delete("session_id");
        const next = sp.toString();
        navigate({ pathname: location.pathname, search: next ? `?${next}` : "" }, { replace: true });
        const t = setTimeout(() => setHint(""), 6000);
        return () => clearTimeout(t);
      }
    } catch {}
  }, [location.search, location.pathname, navigate]); // <-- added location.pathname

  useEffect(() => {
    try {
      const sp = new URLSearchParams(location.search || "");
      const invite = (sp.get("invite") || "").trim();
      if (invite) localStorage.setItem("pendingTenantInviteToken", invite);
    } catch {}
  }, [location.search]);

  // If we arrive via Firebase email link with reset password, keep previous behavior
  useEffect(() => {
    const s = new URLSearchParams(location.search);
    const mode = s.get("mode");
    if (mode === "resetPassword") {
      navigate(`/reset-password?${s.toString()}`, { replace: true });
    }
  }, [location.search, navigate]);

  // ----- Plan/price from /register?plan=...&backendPlan=...&price=... -----
  const qs = new URLSearchParams(location.search);
  const planQ = qs.get("plan") || "";
  const backendPlanQ = qs.get("backendPlan") || "";
  const priceQ = qs.get("price") || "";
  const signupHint = qs.get("signup") === "1";
  const hasPlan = !!priceQ;

  // -------------------- SIGN IN STATE --------------------
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [err, setErr] = useState("");
  const [hint, setHint] = useState("");
  const [busy, setBusy] = useState(false);

  // -------------------- SIGN UP STATE --------------------
  const [mode, setMode] = useState(() => (hasPlan || signupHint ? "signup" : "signin"));
  const [sEmail, setSEmail] = useState("");
  const [sPassword, setSPassword] = useState("");
  const [sName, setSName] = useState("");
  const [sBusy, setSBusy] = useState(false);
  const [sAgreed, setSAgreed] = useState(false);

  function suppressStripeRedirectFor(minutes = 10) {
    try {
      const until = Date.now() + minutes * 60 * 1000;
      sessionStorage.setItem("suppressStripeRedirectUntil", String(until));
    } catch {}
  }

  function getPendingTenantInviteToken() {
    try {
      const sp = new URLSearchParams(window.location.search || "");
      return (
        (sp.get("invite") || "").trim() ||
        localStorage.getItem("pendingTenantInviteToken") ||
        ""
      );
    } catch {
      return "";
    }
  }

  function clearPendingTenantInviteToken() {
    try { localStorage.removeItem("pendingTenantInviteToken"); } catch {}
  }

  async function acceptPendingTenantInvite(user) {
    const token = getPendingTenantInviteToken();
    if (!token || !user) return false;

    try {
      const idToken = await user.getIdToken();
      const res = await fetch(
        "https://us-central1-lastwishbox-business.cloudfunctions.net/acceptTenantInvite",
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            "Authorization": `Bearer ${idToken}`
          },
          body: JSON.stringify({ token })
        }
      );

      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        throw new Error(data?.error || "Failed to accept tenant invite");
      }

      clearPendingTenantInviteToken();
      return true;
    } catch (e) {
      setErr(e?.message || "Failed to accept invite");
      return false;
    }
  }

  // --- Profile bootstrap (idempotent; keep your schema) ---
  async function ensureProfile(user) {
    const ref = doc(db, "users", user.uid);
    const snap = await getDoc(ref);
    if (!snap.exists()) {
      await setDoc(ref, {
        username: user.email || "",
        email: user.email || "",
        name: sName || "",
        phone: "",
        address: "",
        createdAt: serverTimestamp(),
        subscriptionStatus: "pending",

        // New unified account model (backward-compatible)
        accountTypes: {
          personal: true,
          business: false,
          manager: false,
        },
        personalAccount: {
          active: false,
          plan: "",
          status: "pending",
        },
        businessAccount: {
          active: false,
          tenantId: "",
          role: "",
          billingStatus: "",
        },
        managerAccess: {
          active: false,
          readOnly: false,
        },
      }, { merge: true });
    }
  }

  // Try to mark user active if they returned from Stripe (existing behavior preserved)

  // ---------- ONLY CHANGE: prioritize manager-only → /manager-select ----------
  async function afterSignIn(user) {
    await ensureProfile(user);
    try {
      const snap = await getDoc(doc(db, "users", user.uid));
      const profileData = snap.exists() ? (snap.data() || {}) : {};

      // New unified account model first
      const accountTypes = profileData.accountTypes || {};
      const personalAccount = profileData.personalAccount || {};
      const businessAccount = profileData.businessAccount || {};
      const managerAccess = profileData.managerAccess || {};

      // Legacy fallbacks
      const tenantId =
        businessAccount.tenantId ||
        profileData.primaryTenantId ||
        profileData.tenantId ||
        "";

      const tenantRole = String(
        businessAccount.role ||
        profileData.tenantRole ||
        profileData.role ||
        ""
      ).toLowerCase();

      const tenantBillingStatus = String(
        businessAccount.billingStatus ||
        profileData.tenantBillingStatus ||
        ""
      ).toLowerCase();

      const isManagerOnly =
        accountTypes.manager === true ||
        managerAccess.active === true ||
        profileData.managerOnly === true ||
        profileData.role === "account_manager";

      const isPersonalActive =
        personalAccount.active === true ||
        personalAccount.status === "active" ||
        profileData.subscriptionStatus === "active" ||
        !!profileData.isAdmin;

      const isBusinessMember =
        accountTypes.business === true ||
        (!!tenantId && ["owner", "hr_admin", "employee"].includes(tenantRole));

      const hasPaidBusinessAccess =
        (businessAccount.active === true) ||
        (isBusinessMember && ["active", "trial"].includes(tenantBillingStatus));

      if (isManagerOnly && !isPersonalActive && !hasPaidBusinessAccess) {
        navigate("/manager-select", { replace: true });
        return;
      }

      if (tenantId && ["owner", "hr_admin"].includes(tenantRole)) {
        navigate("/", { replace: true });
        return;
      }

      const redirectParam = new URLSearchParams(window.location.search || "").get("redirect") || "";
      const requestedFrom =
        (location.state && location.state.from) ||
        (redirectParam.startsWith("/") ? redirectParam : "") ||
        "/";

      const safeFrom =
        requestedFrom === "/payment-required"
          ? "/"
          : requestedFrom;

      if (isPersonalActive || hasPaidBusinessAccess) {
        navigate(safeFrom, { replace: true });
        return;
      }

      navigate("/payment-required", { replace: true });
    } catch (e) {
      navigate("/payment-required", { replace: true });
    }
  }

  // -------------------- SIGN IN --------------------
async function submit(e) {
  e.preventDefault();
  setErr("");
  setHint("");
  setBusy(true);

  try {
    const cred = await signInWithEmailAndPasswordDebug(auth, email.trim(), password);
    const user = cred.user;

    // TEMP DEV BYPASS: skip email verification in business app
    // if (!user.emailVerified) {
    //   setBusy(false);
    //   setErr("Please verify your email first. Check your inbox.");
    //   return;
    // }

    await acceptPendingTenantInvite(user);
    await afterSignIn(user);
    } catch (e2) {
      const code = String(e2.code || "").toLowerCase();

      // IMPORTANT: stay on the login page — do NOT switch to signup
      if (
        code.includes("auth/invalid-credential") ||
        code.includes("auth/wrong-password") ||
        code.includes("auth/user-not-found") ||
        code.includes("auth/invalid-login-credentials")
      ) {
        try {
          const methods = await fetchSignInMethodsForEmail(auth, email.trim());
          if (!methods || methods.length === 0) {
            setErr("We couldn’t find an account with that email.");
            setHint("");
          } else {
            setErr("Email or password is incorrect.");
            setHint("");
          }
        } catch {
          setErr("Email or password is incorrect.");
          setHint("");
        }
      } else if (code.includes("auth/too-many-requests")) {
        setErr("Too many attempts. Please try again later or reset your password.");
        setHint("");
      } else {
        const msg = friendlyAuthMessage(e2.code) || friendlyFromError(e2, "We could not sign you in. Please try again.");
        setErr(msg);
        setHint("");
      }
      setBusy(false);
    }
  }

  // eslint-disable-next-line no-unused-vars

  // -------------------- SIGN UP --------------------
  async function startCheckoutForNewUser(userEmail, priceId, plan, backendPlan) {
      if (isHostedCheckoutLink(priceId)) {
      window.location.assign(priceId);
      return;
    }

    // LEADS FUNNEL: mark checkout started (best-effort)
    try {
      const e = (userEmail || "").trim().toLowerCase();
      if (e) {
        fetch("/api/leads/capture", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            email: e,
            stage: "checkout_started",
            page: window.location.pathname || "/plans",
            referrer: document.referrer || undefined,
          }),
        });
      }
    } catch {}

    // Meta Test Events: pass through ?test_event_code=TESTxxxx so server CAPI appears in Test Events
    const params = new URLSearchParams(window.location.search || "");
    const testEventCode = (params.get("test_event_code") || "").trim();

    const res = await fetch("/api/createCheckoutSession", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        priceId,
        successUrl: window.location.origin + "/post-payment",
        cancelUrl: window.location.origin + "/plans",
        metadata: { plan: plan || backendPlan || "" },
        email: userEmail || undefined,
        test_event_code: testEventCode || undefined,
      }),
    });
    if (!res.ok) {
      const txt = await res.text().catch(() => "");
      throw new Error(`Checkout failed (${res.status}): ${txt || "unknown error"}`);
    }
    const data = await res.json();
    if (data?.sessionId) {
      try { localStorage.setItem("stripe_last_session_id", data.sessionId); } catch {}
    }
    if (!data?.url) throw new Error("Missing checkout URL");

    // LEADS FUNNEL: Stripe session created (best-effort)
    try {
      const e = (userEmail || "").trim().toLowerCase();
      if (e) {
        fetch("/api/leads/capture", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            email: e,
            stage: "checkout_session_created",
            page: window.location.pathname || "/plans",
            referrer: document.referrer || undefined,
          }),
        });
      }
    } catch {}

    window.location.assign(data.url);
  }

  async function submitSignup(e) {
    e.preventDefault();
    setErr(""); setHint(""); setSBusy(true);
    try {
      if (!sEmail || !sPassword) {
        setSBusy(false);
        setErr("Please enter email and password.");
        return;
      }
      if (!sAgreed) {
        setSBusy(false);
        setErr("Please read and agree to the Terms & Conditions and Privacy Policy.");
        return;
      }
      const cred = await createUserWithEmailAndPassword(auth, sEmail.trim(), sPassword);
      const user = cred.user;
      await ensureProfile(user);

      // ✅ Meta Pixel: track real signups as CompleteRegistration (safe, no-op if pixel not present)
      try {
        if (typeof window !== "undefined" && window.fbq) {
          window.fbq("track", "CompleteRegistration");
        }
      } catch {}

      if (hasPlan && priceQ) {
        await startCheckoutForNewUser(user.email, priceQ, planQ, backendPlanQ);
        return; // redirected
      }

      await afterSignIn(user);
    } catch (e2) {
      const msg = friendlyAuthMessage(e2.code) || friendlyFromError(e2, "Couldn’t create your account. Please try again.");
      setErr(msg);
      setSBusy(false);
    }
  }

  // Force the Signup view when plan present (no UI tabs)
  useEffect(() => {
    if (hasPlan || signupHint) setMode("signup");
  }, [hasPlan, signupHint]);

  return (
    <div style={{ minHeight: "100vh", background: "#f6f7fb", fontFamily: "system-ui, -apple-system, sans-serif" }}>
      {/* Top nav */}
      <div style={{ background: "#fff", borderBottom: "1px solid #e8ebf3", padding: "12px 20px" }}>
        <div style={{ maxWidth: 1180, margin: "0 auto", display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12, flexWrap: "wrap" }}>
          <a href="/"><img src="/Lastwishbox-logo.png" alt="LastWish Box" style={{ height: 36, width: "auto" }} /></a>
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center" }}>
            <a href="/personal/plans" style={{ padding: "8px 12px", borderRadius: 8, border: "1px solid #e8ebf3", background: "#fff", color: "#650202", fontWeight: 700, fontSize: 13, textDecoration: "none", whiteSpace: "nowrap" }}>Personal plans</a>
            <a href="/plans" style={{ padding: "8px 12px", borderRadius: 8, border: "1px solid #e8ebf3", background: "#fff", color: "#650202", fontWeight: 700, fontSize: 13, textDecoration: "none", whiteSpace: "nowrap" }}>Business plans</a>
          </div>
        </div>
      </div>

      {/* Card */}
      <div style={{ display: "flex", alignItems: "center", justifyContent: "center", padding: "48px 24px 80px" }}>
        <div style={{ width: "100%", maxWidth: 460, background: "#fff", borderRadius: 20, boxShadow: "0 10px 40px rgba(42,5,22,0.10)", border: "1px solid #e8ebf3", overflow: "hidden" }}>

          {/* Card header */}
          <div style={{ background: "#650202", padding: "28px 32px 24px", textAlign: "center" }}>
            <img src="/Lastwishbox-logo.png" alt="LastWish Box" style={{ height: 42, width: "auto", filter: "brightness(0) invert(1)" }} />
            <h2 style={{ margin: "16px 0 0", color: "#fff", fontSize: 22, fontWeight: 800 }}>
              {mode === "signin" ? "Sign in to your account" : "Create your account"}
            </h2>
          </div>

          {/* Card body */}
          <div style={{ padding: "28px 32px" }}>

            {/* ---------- SIGN IN VIEW ---------- */}
            {mode === "signin" && (
              <>
                <form onSubmit={submit} style={{ display: "flex", flexDirection: "column", gap: 14 }}>
                  <div>
                    <label style={{ display: "block", fontSize: 12, fontWeight: 700, color: "#657899", marginBottom: 6 }}>Email</label>
                    <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} required
                      style={{ width: "100%", height: 44, padding: "0 12px", borderRadius: 10, border: "1px solid #d6d6e7", fontSize: 15, boxSizing: "border-box", outline: "none" }} />
                  </div>
                  <div>
                    <label style={{ display: "block", fontSize: 12, fontWeight: 700, color: "#657899", marginBottom: 6 }}>Password</label>
                    <input type="password" value={password} onChange={(e) => setPassword(e.target.value)} required
                      style={{ width: "100%", height: 44, padding: "0 12px", borderRadius: 10, border: "1px solid #d6d6e7", fontSize: 15, boxSizing: "border-box", outline: "none" }} />
                  </div>
                  <button type="submit" disabled={busy}
                    style={{ height: 46, borderRadius: 10, background: "#980000", border: "none", color: "#fff", fontWeight: 800, fontSize: 15, cursor: busy ? "not-allowed" : "pointer", boxShadow: "0 4px 14px rgba(152,0,0,0.20)", marginTop: 4 }}>
                    {busy ? "Signing in…" : "Sign in"}
                  </button>
                </form>

                {(err || hint) && (
                  <div style={{ marginTop: 12 }}>
                    {err && <div style={{ color: "#a00", fontSize: 14, padding: "8px 10px", background: "#fee2e2", borderRadius: 8 }}>{err}</div>}
                    {hint && <div style={{ color: "#065f46", fontSize: 14, padding: "8px 10px", background: "#ecfdf5", borderRadius: 8, marginTop: 6 }}>{hint}</div>}
                  </div>
                )}

                <button type="button" onClick={() => navigate("/reset-password")}
                  style={{ background: "none", border: "none", color: "#003353", fontWeight: 700, cursor: "pointer", fontSize: 13, padding: "8px 0", textDecoration: "underline" }}>
                  Reset password
                </button>

                <div style={{ borderTop: "1px solid #e8ebf3", margin: "20px 0 16px" }} />

                <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                  <button type="button" onClick={() => navigate("/personal/plans")}
                    style={{ height: 44, borderRadius: 10, background: "#f15822", border: "none", color: "#fff", fontWeight: 800, fontSize: 14, cursor: "pointer" }}>
                    Sign up for a Personal account
                  </button>
                  <button type="button" onClick={() => navigate("/plans")}
                    style={{ height: 44, borderRadius: 10, background: "#003353", border: "none", color: "#fff", fontWeight: 800, fontSize: 14, cursor: "pointer" }}>
                    Sign up for a Business account
                  </button>
                </div>
              </>
            )}

            {/* ---------- SIGN UP VIEW ---------- */}
            {mode === "signup" && (
              <>
                {hasPlan && (
                  <div style={{ background: "#fff0ee", border: "1px solid #f15822", borderRadius: 8, padding: "8px 12px", fontSize: 13, color: "#650202", marginBottom: 14 }}>
                    You selected: <strong>{planQ || backendPlanQ}</strong>. Secure checkout is next.
                  </div>
                )}

                <form onSubmit={submitSignup} style={{ display: "flex", flexDirection: "column", gap: 14 }}>
                  <div>
                    <label style={{ display: "block", fontSize: 12, fontWeight: 700, color: "#657899", marginBottom: 6 }}>Full name (optional)</label>
                    <input type="text" value={sName} onChange={(e) => setSName(e.target.value)} placeholder="Your name"
                      style={{ width: "100%", height: 44, padding: "0 12px", borderRadius: 10, border: "1px solid #d6d6e7", fontSize: 15, boxSizing: "border-box" }} />
                  </div>
                  <div>
                    <label style={{ display: "block", fontSize: 12, fontWeight: 700, color: "#657899", marginBottom: 6 }}>Email</label>
                    <input type="email" value={sEmail} onChange={(e) => setSEmail(e.target.value)} required
                      style={{ width: "100%", height: 44, padding: "0 12px", borderRadius: 10, border: "1px solid #d6d6e7", fontSize: 15, boxSizing: "border-box" }} />
                  </div>
                  <div>
                    <label style={{ display: "block", fontSize: 12, fontWeight: 700, color: "#657899", marginBottom: 6 }}>Password</label>
                    <input type="password" value={sPassword} onChange={(e) => setSPassword(e.target.value)} required
                      style={{ width: "100%", height: 44, padding: "0 12px", borderRadius: 10, border: "1px solid #d6d6e7", fontSize: 15, boxSizing: "border-box" }} />
                  </div>
                  <div style={{ display: "flex", alignItems: "flex-start", gap: 10, marginTop: 4 }}>
                    <input
                      type="checkbox"
                      id="sAgreed"
                      checked={sAgreed}
                      onChange={(e) => setSAgreed(e.target.checked)}
                      style={{ marginTop: 3, accentColor: "#980000", width: 16, height: 16, flexShrink: 0, cursor: "pointer" }}
                    />
                    <label htmlFor="sAgreed" style={{ fontSize: 13, color: "#657899", lineHeight: 1.5, cursor: "pointer" }}>
                      I have read and agree to the{" "}
                      <a href="https://lastwishbox.com/policies/" target="_blank" rel="noopener noreferrer" style={{ color: "#980000", fontWeight: 700 }}>Terms &amp; Conditions</a>
                      {" "}and{" "}
                      <a href="https://lastwishbox.com/policies/" target="_blank" rel="noopener noreferrer" style={{ color: "#980000", fontWeight: 700 }}>Privacy Policy</a>.
                    </label>
                  </div>
                  <button type="submit" disabled={sBusy}
                    style={{ height: 46, borderRadius: 10, background: "#980000", border: "none", color: "#fff", fontWeight: 800, fontSize: 15, cursor: sBusy ? "not-allowed" : "pointer", boxShadow: "0 4px 14px rgba(152,0,0,0.20)", marginTop: 4 }}>
                    {sBusy ? "Creating…" : "Create account"}
                  </button>
                </form>

                {(err || hint) && (
                  <div style={{ marginTop: 12 }}>
                    {err && <div style={{ color: "#a00", fontSize: 14, padding: "8px 10px", background: "#fee2e2", borderRadius: 8 }}>{err}</div>}
                    {hint && <div style={{ color: "#065f46", fontSize: 14, padding: "8px 10px", background: "#ecfdf5", borderRadius: 8, marginTop: 6 }}>{hint}</div>}
                  </div>
                )}

                <div style={{ borderTop: "1px solid #e8ebf3", margin: "20px 0 16px" }} />
                <button type="button" onClick={() => setMode("signin")}
                  style={{ width: "100%", height: 44, borderRadius: 10, border: "1px solid #e8ebf3", background: "#fff", color: "#650202", fontWeight: 700, fontSize: 14, cursor: "pointer" }}>
                  Back to sign in
                </button>
              </>
            )}

          </div>
        </div>
      </div>
    </div>
  );
}
